import { useEffect, useState } from "react";
import AdminNav from "../../components/AdminNav";
import { getAdminRevenue } from "../../api/client";
import LoadingIndicator from "../../components/LoadingIndicator";

const METHOD_LABELS = {
  cash: "Cash on Delivery",
  paystack: "Paystack",
  flutterwave: "Flutterwave",
  wallet: "Wallet",
  b2b_invoice: "B2B Invoice"
};

function naira(n) {
  return `₦${Math.round(n || 0).toLocaleString()}`;
}

// A single headline number — used for the KPI row. `tone` picks a semantic
// accent (never an arbitrary series color) matching what the number means:
// accent for the primary revenue metric, info/success/warning for the rest.
function StatTile({ icon, label, value, sublabel, tone = "default" }) {
  const toneStyle = {
    default: { color: "var(--text-primary)" },
    accent:  { color: "var(--text-accent)" },
    success: { color: "var(--success-hover)" },
    info:    { color: "var(--info-hover)" },
    warning: { color: "var(--warning-hover)" }
  }[tone];

  return (
    <div className="delivery-card" style={{ flex: "1 1 220px", minWidth: "220px" }}>
      <div style={{ fontSize: "13px", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "6px" }}>
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <div style={{ fontSize: "28px", fontWeight: 800, marginTop: "6px", ...toneStyle }}>
        {value}
      </div>
      {sublabel && <p className="hint" style={{ marginTop: "4px" }}>{sublabel}</p>}
    </div>
  );
}

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const { data } = await getAdminRevenue();
        setData(data);
      } catch (err) {
        setError(err.response?.data?.error || "Could not load revenue data.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filteredRiders = (data?.riders || []).filter(r => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      r.name?.toLowerCase().includes(q) ||
      r.phone?.toLowerCase().includes(q) ||
      r.city?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">

        <div className="brand-header">
          <span className="brand-logo">💰</span>
          <span className="brand-name">Revenue Dashboard</span>
          <p className="brand-tagline">System commission, dispatcher wallets, payouts, and cash-on-delivery exposure</p>
        </div>

        {error && <p className="error">{error}</p>}

        {loading ? (
          <LoadingIndicator label="Loading revenue data..." />
        ) : (
          <>
            {/* KPI ROW */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", marginBottom: "24px" }}>
              <StatTile
                icon="💰"
                label="Total System Revenue"
                value={naira(data.summary.totalRevenue)}
                sublabel="Commission earned across every completed order"
                tone="accent"
              />
              <StatTile
                icon="👛"
                label="Digital Wallet Balances"
                value={naira(data.summary.totalDigitalWallet)}
                sublabel="Currently held across all dispatcher wallets, unpaid out"
                tone="info"
              />
              <StatTile
                icon="✅"
                label="Total Payouts Made"
                value={naira(data.summary.totalPayouts)}
                sublabel="Historical payouts settled to dispatchers"
                tone="success"
              />
              <StatTile
                icon="💵"
                label="Cash on Delivery Owed"
                value={naira(data.summary.totalCodOwed)}
                sublabel="Commission dispatchers owe back from cash collected"
                tone="warning"
              />
              <StatTile
                icon="🧾"
                label="B2B/Corporate Outstanding"
                value={naira(data.summary.totalB2BOutstanding)}
                sublabel="Unpaid invoices across all business accounts"
                tone="warning"
              />
            </div>

            {/* REVENUE BY PAYMENT METHOD */}
            <div className="delivery-card" style={{ marginBottom: "24px" }}>
              <h3>Revenue by Payment Method</h3>
              {data.summary.revenueByMethod.length === 0 ? (
                <p className="hint">No commission recorded yet.</p>
              ) : (
                data.summary.revenueByMethod.map(m => (
                  <div className="info-row" key={m.paymentMethod}>
                    <span className="label">{METHOD_LABELS[m.paymentMethod] || m.paymentMethod}</span>
                    <span className="value">{naira(m.amount)} commission · {m.count} order{m.count === 1 ? "" : "s"} · {naira(m.orderTotal)} gross</span>
                  </div>
                ))
              )}
            </div>

            {/* BUSINESSES OWING — B2B/Corporate accounts with an unpaid balance */}
            <div className="delivery-card" style={{ marginBottom: "24px" }}>
              <h3>Businesses Owing</h3>
              <p className="hint" style={{ marginBottom: "12px" }}>Corporate/B2B accounts with an outstanding invoice balance.</p>
              {data.businessesOwing.length === 0 ? (
                <p className="hint">No business currently owes anything.</p>
              ) : (
                data.businessesOwing.map(b => (
                  <div className="info-row" key={b.businessUserId}>
                    <span className="label">
                      {b.name}
                      <span style={{ color: "var(--text-muted)", fontSize: "12px", marginLeft: "6px" }}>
                        ({b.billingMode === "consolidated" ? "Consolidated" : "Pay Now"})
                      </span>
                    </span>
                    <span className="value" style={{ color: "var(--warning-hover)", fontWeight: 700 }}>
                      {naira(b.totalOwed)} · {b.invoiceCount} invoice{b.invoiceCount === 1 ? "" : "s"}
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* PER-RIDER BREAKDOWN */}
            <div className="delivery-card">
              <h3>Dispatcher Breakdown</h3>
              <p className="hint" style={{ marginBottom: "12px" }}>
                Revenue generated, wallet balance, payout history, and COD owed — per dispatcher, highest commission first.
              </p>
              <input
                type="text"
                placeholder="Search by name, phone, or city..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ marginBottom: "16px" }}
              />

              {filteredRiders.length === 0 ? (
                <p className="hint">
                  {data.riders.length === 0 ? "No dispatcher revenue recorded yet." : "No dispatchers match your search."}
                </p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                        <th style={{ padding: "8px" }}>Dispatcher</th>
                        <th style={{ padding: "8px" }}>Orders</th>
                        <th style={{ padding: "8px" }}>Total Revenue</th>
                        <th style={{ padding: "8px" }}>Digital Wallet</th>
                        <th style={{ padding: "8px" }}>Payout</th>
                        <th style={{ padding: "8px" }}>COD Owed</th>
                        <th style={{ padding: "8px" }}>Charges</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRiders.map(r => (
                        <tr key={r.riderId} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "8px" }}>
                            <strong>{r.name}</strong>
                            <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{r.phone} · {r.city}</div>
                          </td>
                          <td style={{ padding: "8px" }}>{r.orderCount}</td>
                          <td style={{ padding: "8px" }}>{naira(r.totalRevenue)}</td>
                          <td style={{ padding: "8px", color: "var(--info-hover)" }}>{naira(r.digitalWallet)}</td>
                          <td style={{ padding: "8px", color: "var(--success-hover)" }}>{naira(r.totalPayout)}</td>
                          <td style={{ padding: "8px", color: "var(--warning-hover)" }}>{naira(r.cashOwed)}</td>
                          <td style={{ padding: "8px", fontWeight: 700, color: "var(--text-accent)" }}>{naira(r.charges)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
